<script setup>
import { ref, watch, onMounted } from 'vue';
import { cn } from '@/lib/utils';

const props = defineProps({
    modelValue: { type: String, default: '' },
    defaultValue: { type: String, default: '' },
    maxHeight: { type: Number, default: Number.MAX_SAFE_INTEGER },
    minHeight: { type: Number, default: 52 },
    class: { type: null, required: false }
});
const emits = defineEmits(['update:modelValue']);

// internal value to allow v-model-like binding
const internalValue = ref(props.modelValue || props.defaultValue);
const textareaRef = ref(null);

// adjust textarea height to content
function autosize() {
    const el = textareaRef.value;
    if (!el) return;
    el.style.height = 'auto';
    const h = Math.min(el.scrollHeight, props.maxHeight);
    el.style.height = `${Math.max(h, props.minHeight)}px`;
}

// sync external modelValue -> internalValue
watch(
    () => props.modelValue,
    (v) => {
        internalValue.value = v;
    }
);

// sync internalValue -> modelValue and autosize
watch(internalValue, (v) => {
    emits('update:modelValue', v);
    autosize();
});

onMounted(autosize);
</script>

<template>
    <textarea
        ref="textareaRef"
        v-model="internalValue"
        class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        :class="cn(props.class)"
        @input="autosize" />
</template>
